import { Modal, SelectInput } from 'melp-design/components';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { ModalProps } from 'store/modal';
import { Controller, SubmitHandler, useForm } from 'react-hook-form-latest';
import { ContentLanguage, uppercaseContentLanguages } from 'types/general';
import { formatLanguage } from 'utils/format';

interface Values {
  language: Uppercase<ContentLanguage>;
}

interface Props extends ModalProps {
  closeModal: (
    param?:
      | { action: 'CLOSE' }
      | { action: 'CONFIRM'; language: Uppercase<ContentLanguage> },
  ) => void;
  languages?: Array<Uppercase<ContentLanguage>>;
  initialLanguage?: Uppercase<ContentLanguage>;
}

export const LanguageSelectModal: FC<Props> = ({
  closeModal,
  initialLanguage,
  languages = uppercaseContentLanguages,
}) => {
  const { t, i18n } = useTranslation();

  const { handleSubmit, control } = useForm<Values>({
    defaultValues: {
      language: initialLanguage ?? languages[0],
    },
  });

  const onSubmit: SubmitHandler<Values> = (values) => {
    closeModal({ action: 'CONFIRM', language: values.language });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Modal.Content
        title={t('marketplaceItems.translationLanguage')}
        actions={[
          {
            variant: 'neutral-outline',
            onClick: () => closeModal({ action: 'CLOSE' }),
            label: t('common.cancel'),
          },
          {
            variant: 'primary',
            label: t('common.confirm'),
            type: 'submit',
          },
        ]}
      >
        <Controller
          name="language"
          control={control}
          render={({ field: { name, value, onChange, ref } }) => (
            <SelectInput
              label={t('marketplaceItems.translationLanguage')}
              name={name}
              value={value}
              onChange={onChange}
              options={languages.map((language) => ({
                value: language,
                label: formatLanguage(i18n.language, language),
              }))}
              ref={ref}
            />
          )}
        />
      </Modal.Content>
    </form>
  );
};
